import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import Page from 'assets/components/page/Page';
import ThemePageError from 'assets/components/page/themes/Theme.Page.Error';
import ThemeTableSimple from 'assets/components/table/themes/Theme.Table.Simple';
import ThemeTagRounded from 'assets/components/tag/themes/Theme.Tag.Rounded';
import useApi from 'assets/hooks/api/useApi';
import { getLocales } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import Theme from 'assets/themes/Theme.Common';
import { defaultDateTimeFormat, localizeDate } from 'assets/utils/data/Date';
import { entries, first, groupBy, sortBy, sumBy, uniq } from 'lodash';
import Claim from 'models/claims/claim/Model.Claim';
import ClaimJob from 'models/claims/claimJob/Model.ClaimJob';
import ClaimJobComment from 'models/claims/claimJobComment/ClaimJobComment';
import useClaimRedirect from 'module/claims/hooks/useClaimRedirect';
import React, { useMemo, useState } from 'react';
import { useClaimJobProvider } from '../ClaimJob.Hooks';
import ThemeWrapperStack from 'assets/themes/wrappers/Theme.Wrapper.Stack';

export function ClaimJobList() {
  const { lang } = getLocales();
  const { listApi, statusInfo, emailTemplates, hasAnyPermission } = useClaimJobProvider();
  const { finalRedirect } = useClaimRedirect({ emailTemplates, statusInfo });
  const [expandedClaimJobId, setExpandedClaimJobId] = useState<number>();

  const claimId = useMemo(() => uniq(listApi?.payload?.data?.map((it) => it.claimId)), [listApi?.payload?.data]);
  const claimListApi = useApi(
    {
      action: Claim.list,
      body: {
        id: claimId,
      },
      wait: !claimId,
      keepResponse: true,
    },
    [JSON.stringify(claimId)]
  );

  return !listApi.payload && !listApi.isExecuting && listApi.apiCounter > 0 ? (
    <Page
      class={ThemePageError}
      sidebar={<Icon class="fas-clipboard-list" />}
      header={lang.claimJobs}
      render={lang.noInfo}
    />
  ) : (
    <table className={ThemeTableSimple}>
      <thead>
        <tr>
          <th>{lang.contract}</th>
          <th>{lang.jobNumber}</th>
          <th>{lang.authorizationNumber}</th>
          <th>{lang.claimNumber}</th>
          <th>{lang.clientName}</th>
          <th>{lang.product}</th>
          <th>{lang.claimType}</th>
          <th>{lang.status}</th>
          <th>{lang.total}</th>
          <th data-actions>{lang.actions}</th>
        </tr>
      </thead>
      <tbody>
        <DataMap
          data={listApi?.payload?.data}
          render={({ data: claimJob }) => {
            const claimJobAggregates = ClaimJob.aggregates(claimJob);
            const groupedByCurrency = entries(groupBy(claimJobAggregates, (i) => i.currency));
            const claim = claimListApi?.payload?.data?.find((it) => it.id === claimJob?.claimId);
            const claimNumber = claimJob?.claim?.number || claim?.number || 'N/A';
            return (
              <React.Fragment key={claimJob?.id}>
                <tr style={{ borderTop: `2px solid ${Theme.Color.shadow}` }}>
                  <td>{claim?.contract?.appNumber}</td>
                  <td>
                    <div
                      style={{
                        display: 'flex',
                        gap: Theme.Size.XS,
                        alignItems: 'center',
                      }}
                    >
                      {claimJob.areAutomationRulesApplied && (
                        <Box
                          class={ThemeTagRounded}
                          style={{
                            backgroundColor: Theme.Color.success,
                            fontSize: Theme.FontSize.S,
                          }}
                        >
                          A
                        </Box>
                      )}
                      <b>
                        {claimNumber || 'N/A'}-J{claimJob?.jobNumber || 'N/A'}
                      </b>
                    </div>
                  </td>
                  <td>{claimJob.authorizationNumber ?? 'N/A'}</td>
                  <td>{claimNumber || 'N/A'}</td>
                  <td>{claim?.contract?.client?.name ?? 'N/A'}</td>
                  <td>{claim?.contract?.product?.name}</td>
                  <td>{claimJob?.claimType?.name}</td>
                  <td style={{ fontSize: Theme.FontSize.S }}>
                    <b>
                      {claimJob?.statusCode ? lang[claimJob?.statusCode]?.replace(/_/g, ' - ') : lang.noStatusDefined}
                    </b>
                  </td>
                  <td>
                    <DataMap
                      data={groupedByCurrency}
                      render={({ data: [currency, items] }) => (
                        <div key={currency}>
                          <FormatValue value={` $${sumBy(items, (it) => it.total).toFixed(2)}${currency}`} />
                        </div>
                      )}
                    />
                  </td>
                  <td>
                    <Box class={ThemeWrapperStack}>
                      <Button
                        onClick={finalRedirect}
                        data={claimJob.claim || claim}
                        class={ThemeButtonCircle}
                        disabled={!hasAnyPermission}
                        active
                        media="fas-folder-open"
                        htmlElementProps={{
                          title: `${lang.open} ${lang.claim}`,
                        }}
                      />
                      <Button
                        onClick={setExpandedClaimJobId}
                        data={expandedClaimJobId === claimJob.id ? null : claimJob.id}
                        class={ThemeButtonCircle}
                        disabled={!hasAnyPermission}
                        active
                        media={
                          <Icon class={expandedClaimJobId === claimJob.id ? 'fas-chevron-up' : 'fas-chevron-down'} />
                        }
                        htmlElementProps={{
                          title: expandedClaimJobId === claimJob.id ? lang.viewLess : lang.viewMore,
                        }}
                      />
                    </Box>
                  </td>
                </tr>
                {expandedClaimJobId === claimJob.id && <ClaimJobComments claimJob={claimJob} />}
              </React.Fragment>
            );
          }}
        />
      </tbody>
    </table>
  );
}
export function ClaimJobListInline({ claim }: { claim: Claim }) {
  const { lang } = getLocales();

  const jobsApi = useApi(
    {
      action: ClaimJob.list,
      body: { claimId: claim.id },
      wait: !claim.id,
      keepResponse: true,
    },
    [claim.id]
  );
  return (
    <>
      <tr>
        <td>{lang.jobNumber}</td>
        <td>{lang.claimType}</td>
        <td>{lang.decidedDate}</td>
        <td>{lang.paymentDate}</td>
        <td>{lang.lastModified}</td>
        <td>{lang.status}</td>
        <td>{lang.amountClaimed}</td>
        <td></td>
      </tr>
      <DataMap
        data={sortBy(jobsApi.payload?.data ?? [], (it) => it.jobNumber)}
        render={({ data: claimJob }) => {
          const claimJobAggregates = ClaimJob.aggregates(claimJob);
          const groupedByCurrency = entries(groupBy(claimJobAggregates, (i) => i.currency));

          return (
            <React.Fragment key={claimJob.id}>
              <tr>
                <td>
                  <b>
                    {claim.number}-J{claimJob.jobNumber}
                  </b>
                </td>
                <td>{claimJob.claimType.name}</td>
                <td>
                  {claimJob?.decidedDateUtc ? localizeDate(claimJob?.decidedDateUtc, defaultDateTimeFormat) : 'N/A'}
                </td>
                <td>
                  {claimJob.paymentDateUtc ? localizeDate(claimJob?.paymentDateUtc, defaultDateTimeFormat) : 'N/A'}
                </td>
                <td>{claimJob.modifiedAtUtc ? localizeDate(claimJob?.modifiedAtUtc, defaultDateTimeFormat) : 'N/A'}</td>
                <td style={{ fontSize: Theme.FontSize.S }}>
                  <b>{claimJob.statusCode ? lang[claimJob.statusCode]?.replace(/_/g, ' - ') : lang.noStatusDefined}</b>
                </td>
                <td>
                  <DataMap
                    data={groupedByCurrency}
                    render={({ data: [currency, items] }) => (
                      <div key={currency}>
                        <FormatValue value={`${sumBy(items, (it) => it.total).toFixed(2)}${currency}`} />
                      </div>
                    )}
                  />
                </td>
                <td></td>
              </tr>
              <ClaimJobComments claimJob={claimJob} />
            </React.Fragment>
          );
        }}
      />
    </>
  );
}
function ClaimJobComments({ claimJob }: { claimJob: Model.IClaimJob }) {
  const { lang } = getLocales();
  const { permissions } = useAuthStore();
  const canViewClaimJobComments =
    permissions?.CLAIM_ADMIN ||
    permissions?.CREATE_CLAIMJOBCOMMENT ||
    permissions?.VIEW_CLAIMJOBCOMMENT ||
    permissions?.EDIT_CLAIMJOBCOMMENT ||
    permissions?.DELETE_CLAIMJOBCOMMENT;

  const claimJobId = claimJob?.id ? [claimJob?.id] : undefined;
  const latestClaimJobCommentApi = useApi(
    {
      action: ClaimJobComment.list,
      body: { claimJobId, latestPerJob: true },
      wait: !claimJobId || !canViewClaimJobComments,
      keepResponse: true,
    },
    [JSON.stringify(claimJobId), canViewClaimJobComments]
  );

  return (
    <tr>
      <td colSpan={8}>
        <b>{lang.latestNote}: </b>
        <span>
          {latestClaimJobCommentApi?.isExecuting
            ? lang.loading
            : (first(latestClaimJobCommentApi?.payload?.data?.filter((c) => c.claimJobId === claimJob.id))?.comment ??
              'N/A')}
        </span>
        <br />
        <b>{lang.customerComplaint}: </b>
        <span>{claimJob.customerComplaint ?? 'N/A'}</span>
        <br />
        <b>{lang.causeOfFailure}: </b>
        <span>{claimJob.cause ?? 'N/A'}</span>
        <br />
        <b>{lang.correction}: </b> <span>{claimJob.correction ?? 'N/A'}</span>
      </td>
    </tr>
  );
}
